/**
 * «Repetir esta campaña» (`campanas/:id/repetir`, 28/10/2026). Una página, no una ventana encima: se elige cada cuánto
 * (cada semana, cada 2 semanas o cada mes), qué día, a qué hora y en qué zona, hasta cuándo (una fecha o cuántas veces) y
 * si sale sola, y se ven «Las próximas» como las calcula el backend (en la zona elegida, con el fin de mes).
 *
 * - Cada repetición es una campaña nueva con la configuración y la audiencia de ésta (que se vuelve a calcular). La arma
 *   el programador unas horas antes de su hora (`anticipacionHoras`) y la chequea.
 * - «Que salga sola si está todo en orden», apagada por defecto: prendida, sale a su hora si la plantilla sigue
 *   aprobada, hay saldo, el chequeo no tiene bloqueos y la audiencia no creció a más del doble (sin una prueba nueva: ya
 *   se probó en ésta). Si falta algo, o con la casilla apagada, queda en borrador y se avisa al equipo.
 * - La fatiga (29/10/2026): las mismas personas la reciben cada vez. Lo que mandaron la original y las repeticiones
 *   anteriores no frena a la que sigue; lo que mandó cualquier otra campaña, sí. El backend lo dice en `advertencias`.
 * - Es del equipo: la ruta va con `PaginaSoloEquipo` y el backend le niega todo a una persona de un cliente. Un
 *   seguimiento o una repetición no se repiten: la página dice por qué.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CalendarClock, Info, Loader2, Repeat } from "lucide-react";
import { oficialApi } from "../api";
import type { CadaRepeticion, CampanaDetalle, OpcionesDeRepeticion } from "../types";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { CADAS, DIAS_SEMANA, PROXIMAS_A_MOSTRAR, VECES_MAX, fechaDeLaSerie, reglaEnEdicion, reglaParaEnviar, type ReglaEnEdicion } from "../logic/repeticion";
import { rutaCampana } from "../logic/seguimiento";
import { ZONAS, ZONA_MARCA, etiquetaZona, paisDeZona } from "../logic/zonas";
import { Tarjeta } from "../components/Tarjeta";

const campo = "h-9 rounded-lg border border-slate-200 px-2 text-sm text-slate-800 bg-white";

export default function CampanaRepetirPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [datos, setDatos] = useState<OpcionesDeRepeticion | null>(null);
  const [error, setError] = useState("");
  const [noEncontrada, setNoEncontrada] = useState(false);
  const [regla, setRegla] = useState<ReglaEnEdicion | null>(null);
  const [plan, setPlan] = useState<Pick<OpcionesDeRepeticion, "proximas" | "advertencias" | "error"> | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState("");
  const pedido = useRef(0);

  // La campaña, la que tiene y la sugerida: la regla arranca con una de las dos.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const l = leerRespuesta<OpcionesDeRepeticion>(await oficialApi.opcionesRepeticion(id));
        if (!vivo) return;
        if (!l.ok) { setError(textoError(l)); return; }
        setDatos(l.data);
        setRegla(reglaEnEdicion(l.data.actual ?? l.data.sugerida));
      } catch (e) {
        if (!vivo) return;
        const x = leerError(e);
        if (x.status === 404) setNoEncontrada(true);
        else setError(textoError(x));
      }
    })();
    return () => { vivo = false; };
  }, [id]);

  const enviar = useMemo(() => (regla ? reglaParaEnviar(regla) : null), [regla]);
  const clave = enviar ? JSON.stringify(enviar) : "";

  // Cada cambio vuelve a pedir las próximas (la respuesta vieja de un cambio anterior no pisa la nueva).
  useEffect(() => {
    if (!enviar || !datos || datos.motivo) { setPlan(null); return; }
    const n = ++pedido.current;
    setCalculando(true);
    (async () => {
      try {
        const l = leerRespuesta<OpcionesDeRepeticion>(await oficialApi.opcionesRepeticion(id, enviar));
        if (n !== pedido.current) return;
        setPlan(l.ok ? { proximas: l.data.proximas, advertencias: l.data.advertencias, error: l.data.error } : { proximas: [], advertencias: [], error: textoError(l) });
      } catch (e) {
        if (n === pedido.current) setPlan({ proximas: [], advertencias: [], error: textoError(leerError(e)) });
      } finally {
        if (n === pedido.current) setCalculando(false);
      }
    })();
    // `clave` resume `enviar`: el efecto corre cuando cambia la regla, no en cada render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, clave, datos]);

  const cambiar = (x: Partial<ReglaEnEdicion>) => setRegla((r) => (r ? { ...r, ...x } : r));

  const guardar = async () => {
    if (!enviar || guardando) return;
    setGuardando(true);
    setErrorGuardar("");
    try {
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.programarRepeticion(id, enviar));
      if (!l.ok) { setErrorGuardar(textoError(l)); return; }
      navigate(rutaCampana(id));
    } catch (e) {
      setErrorGuardar(textoError(leerError(e)));
    } finally {
      setGuardando(false);
    }
  };

  // en su propio renglón: al lado del título (inline) quedaba pegado al ícono («campaña⇄», prueba a mano del 28/10)
  const volver = (
    <div>
      <Link to={rutaCampana(id)} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
        <ArrowLeft size={13} /> Volver a la campaña
      </Link>
    </div>
  );

  if (noEncontrada) {
    return (
      <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center">
        <p className="text-sm text-slate-600">No encontramos esta campaña.</p>
        <Link to="/activa/oficial/campanas" className="mt-3 inline-block text-sm font-semibold text-violet-600 hover:underline">Volver a las campañas</Link>
      </div>
    );
  }
  if (!datos || !regla) {
    return (
      <div className="space-y-4">
        {volver}
        {error ? (
          <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-red-700" role="alert">
            <p className="font-semibold">No pudimos traer la campaña.</p>
            <p className="mt-1">{error}</p>
          </div>
        ) : (
          <div className="h-64 rounded-2xl bg-slate-100 animate-pulse" aria-busy="true" />
        )}
      </div>
    );
  }

  const c = datos.campana;
  const cabecera = (
    <div className="space-y-2">
      {volver}
      <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight inline-flex items-center gap-2">
        <Repeat size={20} className="text-violet-600" aria-hidden="true" /> {datos.actual ? "Cambiar la repetición" : "Repetir esta campaña"}
      </h2>
      <p className="text-sm text-slate-500">
        De «{c.nombre}»: cada repetición es una campaña nueva con la misma plantilla, las mismas variables, el mismo horario y la
        misma audiencia, calculada de nuevo en cada una (si el público cambia, le llega a la gente de ese momento).
      </p>
    </div>
  );

  if (datos.motivo) {
    return (
      <div className="space-y-5">
        {cabecera}
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="alert">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> <p>{datos.motivo}</p>
        </div>
      </div>
    );
  }

  const zona = regla.zona || ZONA_MARCA;
  const diaMes = Number(regla.diaMes);
  const errorPlan = plan?.error || "";
  const motivoBoton = !enviar ? "Completá cada cuánto, el día, la hora y hasta cuándo"
    : calculando ? "Calculando las próximas…"
    : errorPlan ? errorPlan
    : !plan?.proximas.length ? "No queda ninguna repetición"
    : "";

  return (
    <div className="space-y-5">
      {cabecera}

      <Tarjeta titulo="¿Cada cuánto?">
        <fieldset className="space-y-4">
          <legend className="sr-only">Cada cuánto se repite</legend>
          <div className="flex gap-2 flex-wrap" role="radiogroup" aria-label="Cada cuánto">
            {CADAS.map((x) => (
              <label key={x.valor} className={`h-9 px-3 rounded-lg border text-sm inline-flex items-center gap-2 cursor-pointer ${regla.cada === x.valor ? "border-violet-300 bg-violet-50 text-violet-800 font-semibold" : "border-slate-200 text-slate-700"}`}>
                <input type="radio" name="cada" value={x.valor} checked={regla.cada === x.valor} onChange={() => cambiar({ cada: x.valor as CadaRepeticion })} />
                {x.titulo}
              </label>
            ))}
          </div>

          <div className="flex items-end gap-4 flex-wrap">
            {regla.cada === "mes" ? (
              <label className="text-sm text-slate-700 space-y-1">
                <span className="block text-xs font-semibold text-slate-500">El día del mes</span>
                <input type="number" min={1} max={31} inputMode="numeric" value={regla.diaMes} onChange={(ev) => cambiar({ diaMes: ev.target.value })}
                  aria-label="Día del mes" className={`${campo} w-20`} />
              </label>
            ) : (
              <label className="text-sm text-slate-700 space-y-1">
                <span className="block text-xs font-semibold text-slate-500">El día</span>
                <select value={regla.diaSemana} onChange={(ev) => cambiar({ diaSemana: Number(ev.target.value) })} aria-label="Día de la semana" className={campo}>
                  {DIAS_SEMANA.map((d) => <option key={d.valor} value={d.valor}>{d.titulo}</option>)}
                </select>
              </label>
            )}
            <label className="text-sm text-slate-700 space-y-1">
              <span className="block text-xs font-semibold text-slate-500">A las</span>
              <input type="time" value={regla.hora} onChange={(ev) => cambiar({ hora: ev.target.value })} aria-label="Hora" className={campo} />
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="block text-xs font-semibold text-slate-500">Hora de</span>
              <select value={zona} onChange={(ev) => cambiar({ zona: ev.target.value })} aria-label="Zona horaria" className={campo}>
                {ZONAS.map((z) => <option key={z.id} value={z.id}>{etiquetaZona(z.id)}</option>)}
              </select>
            </label>
          </div>
          {regla.cada === "mes" && diaMes > 28 && (
            <p className="text-xs text-slate-500">En los meses que no tienen el día {diaMes}, sale el último día del mes (por ejemplo, el 30/11 o el 28/02).</p>
          )}
          {regla.cada === "2semanas" && <p className="text-xs text-slate-500">Cada 2 semanas cuenta desde la primera: pausarla y reanudarla no corre la quincena.</p>}
        </fieldset>
      </Tarjeta>

      <Tarjeta titulo="¿Hasta cuándo?">
        <fieldset className="space-y-3">
          <legend className="sr-only">Hasta cuándo se repite</legend>
          <label className="flex items-center gap-2 text-sm text-slate-700 flex-wrap">
            <input type="radio" name="fin" checked={regla.fin === "veces"} onChange={() => cambiar({ fin: "veces" })} />
            <input type="number" min={1} max={VECES_MAX} inputMode="numeric" value={regla.veces} onChange={(ev) => cambiar({ veces: ev.target.value, fin: "veces" })}
              aria-label="Cuántas veces" className={`${campo} w-20`} />
            {Number(regla.veces) === 1 ? "vez" : "veces"} <span className="text-xs text-slate-500">(sin contar ésta)</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 flex-wrap">
            <input type="radio" name="fin" checked={regla.fin === "hasta"} onChange={() => cambiar({ fin: "hasta" })} />
            Hasta el
            <input type="date" value={regla.hasta} onChange={(ev) => cambiar({ hasta: ev.target.value, fin: "hasta" })} aria-label="Hasta el día" className={campo} />
            <span className="text-xs text-slate-500">(inclusive, en hora de {paisDeZona(zona)})</span>
          </label>
        </fieldset>
      </Tarjeta>

      <Tarjeta titulo="¿Sale sola?">
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={regla.saleSola} onChange={(ev) => cambiar({ saleSola: ev.target.checked })} className="mt-1" />
          <span>
            Que salga sola si está todo en orden
            <span className="block text-xs text-slate-500 mt-0.5">
              {regla.saleSola
                ? `Cada repetición se arma ${datos.anticipacionHoras} h antes y se chequea. Sale sola a su hora si la plantilla sigue aprobada, hay saldo para su costo máximo, el chequeo no tiene bloqueos y la audiencia no creció a más del doble que la última que salió. No pide una prueba nueva: la plantilla ya se probó en esta campaña. Si falta algo, queda en borrador y avisamos al equipo.`
                : `Apagada: cada repetición se arma ${datos.anticipacionHoras} h antes, queda en borrador y avisamos al equipo para que la revise y la confirme.`}
            </span>
          </span>
        </label>
      </Tarjeta>

      <Tarjeta>
        <div className="flex items-start gap-3" aria-live="polite">
          <CalendarClock size={18} className="mt-0.5 text-violet-600 flex-shrink-0" aria-hidden="true" />
          <div className="min-w-0 space-y-2">
            {errorPlan ? (
              <p className="text-sm text-red-700" role="alert">{errorPlan}</p>
            ) : !plan ? (
              <p className="text-sm text-slate-500">{calculando ? "Calculando las próximas…" : "Completá la regla para ver las próximas."}</p>
            ) : (
              <p className="text-sm text-slate-800" data-testid="proximas">
                <span className="font-semibold">Las próximas:</span>{" "}
                {plan.proximas.map((p) => fechaDeLaSerie(p, zona)).join(", ")}
                {(regla.fin === "veces" ? Number(regla.veces) > plan.proximas.length : plan.proximas.length >= PROXIMAS_A_MOSTRAR) ? ", …" : ""}
                {zona !== ZONA_MARCA && <span className="text-slate-500"> (hora de {paisDeZona(zona)})</span>}
                {calculando && <Loader2 size={13} className="inline ml-2 animate-spin text-slate-400" />}
              </p>
            )}
            {plan?.advertencias.map((a) => (
              <p key={a} className="flex items-start gap-1.5 text-xs text-amber-800"><Info size={13} className="mt-0.5 flex-shrink-0" aria-hidden="true" /> {a}</p>
            ))}
            <p className="text-xs text-slate-500">
              El consentimiento, las bajas, la franja y el cupo de Meta se respetan como en cualquier campaña, y la fatiga también, salvo la de esta misma serie. Se pausa o se termina desde el detalle.
            </p>
          </div>
        </div>
      </Tarjeta>

      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={() => void guardar()} disabled={!!motivoBoton || guardando}
          className="h-10 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-1.5 disabled:opacity-50">
          {guardando ? <Loader2 size={14} className="animate-spin" /> : <Repeat size={14} />} {guardando ? "Guardando…" : datos.actual ? "Guardar los cambios" : "Programar la repetición"}
        </button>
        {motivoBoton && !guardando && !errorPlan && <span className="text-xs text-slate-500">{motivoBoton}</span>}
        {errorGuardar && <span className="text-xs text-red-700" role="alert">{errorGuardar}</span>}
      </div>
    </div>
  );
}
